import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BatteryCharging, Save } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DynamicFields from "@/components/DynamicFields";
import api from "@/lib/apiClient";

const initial = {
  serial_number: "", model: "", capacity: "", manufacture_date: "", purchase_date: "",
  warranty_months: 36, customer_name: "", customer_phone: "", customer_email: "",
  customer_address: "", customer_city: "", customer_pincode: "", channel: "B2C",
  invoice_no: "", notes: "",
};

function Field({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
      {children}
    </div>
  );
}

export default function RegisterBattery() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const [custom, setCustom] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setCustomField = (k, v) => setCustom((c) => ({ ...c, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.serial_number || !form.model || !form.purchase_date || !form.customer_name || !form.customer_phone) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/batteries", { ...form, warranty_months: Number(form.warranty_months), custom });
      toast.success("Battery registered successfully.");
      navigate("/batteries");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to register battery.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="register-battery-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center">
          <BatteryCharging className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-extrabold">Register Battery</h1>
          <p className="text-muted-foreground">Record a battery sale to activate its warranty.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Battery Details</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Serial Number" required>
              <Input data-testid="serial-input" value={form.serial_number} onChange={set("serial_number")} placeholder="GRB1234" />
            </Field>
            <Field label="Model" required>
              <Input data-testid="model-input" value={form.model} onChange={set("model")} placeholder="GR-LFP-100" />
            </Field>
            <Field label="Capacity">
              <Input value={form.capacity} onChange={set("capacity")} placeholder="100Ah LiFePO4" />
            </Field>
            <Field label="Warranty (months)">
              <Input type="number" data-testid="warranty-months-input" value={form.warranty_months} onChange={set("warranty_months")} />
            </Field>
            <Field label="Manufacture Date">
              <Input type="date" value={form.manufacture_date} onChange={set("manufacture_date")} />
            </Field>
            <Field label="Purchase Date" required>
              <Input type="date" data-testid="purchase-date-input" value={form.purchase_date} onChange={set("purchase_date")} />
            </Field>
            <Field label="Invoice No.">
              <Input value={form.invoice_no} onChange={set("invoice_no")} placeholder="INV-2001" />
            </Field>
            <Field label="Sales Channel">
              <Select value={form.channel} onValueChange={set("channel")}>
                <SelectTrigger data-testid="channel-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="B2C">B2C (Direct Customer)</SelectItem>
                  <SelectItem value="B2B">B2B (Business)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Customer Details</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Customer Name" required>
              <Input data-testid="customer-name-input" value={form.customer_name} onChange={set("customer_name")} />
            </Field>
            <Field label="Phone" required>
              <Input data-testid="customer-phone-input" value={form.customer_phone} onChange={set("customer_phone")} />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.customer_email} onChange={set("customer_email")} />
            </Field>
            <Field label="City">
              <Input value={form.customer_city} onChange={set("customer_city")} />
            </Field>
            <Field label="Pincode">
              <Input value={form.customer_pincode} onChange={set("customer_pincode")} />
            </Field>
            <Field label="Address">
              <Input value={form.customer_address} onChange={set("customer_address")} />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Notes">
              <Textarea value={form.notes} onChange={set("notes")} placeholder="Any additional details..." />
            </Field>
          </div>
          <DynamicFields entity="battery" values={custom} onChange={setCustomField} />
        </Card>

        <div className="flex gap-3">
          <Button type="submit" disabled={saving} className="gap-2" data-testid="submit-battery-button">
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Register Battery"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
